import React, { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Award, Briefcase, CheckCircle2, ClipboardList, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useThemeStore } from "@/store/themeStore";
import EventChip, { type ChipTone } from "@/components/shared/EventChip";
import BrandMark from "./BrandMark";

const quotes = [
  {
    text: "Taskpart gave me the bridge I needed from classroom theory to practical banking experience.",
    author: "Amina J.",
    role: "CRDB Bank Intern",
  },
  {
    text: "A fantastic platform that matches real ambition with genuine industry needs.",
    author: "David M.",
    role: "Engineering Graduate",
  },
  {
    text: "The structured work logs kept me accountable and guided my mentorship correctly.",
    author: "Fatuma H.",
    role: "Azam Media",
  },
];

const logLines: { t: string; k: string; v: string; tone: ChipTone; icon: React.ReactNode }[] = [
  { t: "09:02", k: "Placement matched", v: "Software Engineering · Tigo", tone: "lavender", icon: <Briefcase /> },
  { t: "11:40", k: "Work log submitted", v: "8.0h · awaiting supervisor", tone: "peach", icon: <ClipboardList /> },
  { t: "14:15", k: "Approved by mentor", v: "+8.0h verified", tone: "mint", icon: <CheckCircle2 /> },
  { t: "16:30", k: "Badge earned", v: "Consistent Contributor", tone: "lilac", icon: <Award /> },
];

/** Two-column auth frame: the form on the left, product story on the right (desktop only). */
export default function AuthLayout({ children }: { children: React.ReactNode }) {
  const [activeQuote, setActiveQuote] = useState(0);
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  useEffect(() => {
    const interval = setInterval(() => setActiveQuote((prev) => (prev + 1) % quotes.length), 6000);
    return () => clearInterval(interval);
  }, []);

  const quote = quotes[activeQuote];

  return (
    <div className="grid min-h-dvh bg-background text-foreground lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className="flex min-h-dvh flex-col">
        <header className="flex h-16 items-center justify-between px-5 sm:px-8">
          <Link
            to="/"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <BrandMark />
          </Link>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={mode === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              className="rounded-full border border-border text-body hover:text-foreground"
            >
              {mode === "dark" ? <Sun /> : <Moon />}
            </Button>
            <Button variant="ghost" size="sm" asChild className="text-muted-foreground">
              <Link to="/">
                <ArrowLeft />
                Home
              </Link>
            </Button>
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-[400px]"
          >
            {children}
          </motion.div>
        </main>

        <footer className="px-5 pb-6 text-caption text-muted-foreground sm:px-8">
          © {new Date().getFullYear()} Taskpart · Dar es Salaam
        </footer>
      </div>

      <aside className="relative hidden p-3 lg:flex">
        <div className="relative flex w-full flex-col justify-between gap-10 overflow-hidden rounded-3xl bg-shell p-12 xl:p-14">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 -right-24 size-[420px] rounded-full bg-pastel-lavender opacity-70 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-40 -left-24 size-[380px] rounded-full bg-pastel-peach opacity-60 blur-3xl"
          />

          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-xs font-medium text-body shadow-soft">
              <span className="size-1.5 rounded-full bg-success" aria-hidden />
              Placement operations platform
            </span>
            <h2 className="mt-6 max-w-lg text-[44px] leading-[50px] font-medium tracking-[-1.4px] text-foreground">
              Connect with{" "}
              <span className="font-serif text-[52px] font-normal italic tracking-[-0.5px]">
                opportunities
              </span>
              .
            </h2>
            <p className="mt-4 max-w-md text-body-md text-body">
              The platform bridging university talent with industry placements and professional
              mentorship.
            </p>
          </div>

          <div className="relative rounded-2xl border border-border bg-card p-5 shadow-panel">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[15px] font-medium text-foreground">Today&apos;s activity</p>
              <span className="rounded-full bg-muted px-3 py-1 text-xs text-body">Live</span>
            </div>
            <ul className="mt-4 space-y-2.5">
              {logLines.map((line) => (
                <li key={line.t} className="flex items-center gap-3">
                  <span className="w-11 shrink-0 text-xs text-muted-foreground tabular-nums">{line.t}</span>
                  <EventChip tone={line.tone} icon={line.icon} title={line.k} meta={line.v} className="flex-1" />
                </li>
              ))}
            </ul>
          </div>

          <figure className="relative min-h-[120px]" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeQuote}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3 }}
              >
                <blockquote className="max-w-lg text-body-lg text-body-strong">
                  “{quote.text}”
                </blockquote>
                <figcaption className="mt-3 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{quote.author}</span> · {quote.role}
                </figcaption>
              </motion.div>
            </AnimatePresence>
            <div className="mt-5 flex gap-1.5" role="tablist" aria-label="Testimonials">
              {quotes.map((q, i) => (
                <button
                  key={q.author}
                  type="button"
                  role="tab"
                  aria-selected={i === activeQuote}
                  aria-label={`Show testimonial from ${q.author}`}
                  onClick={() => setActiveQuote(i)}
                  className={`h-1.5 rounded-full transition-all ${i === activeQuote ? "w-7 bg-foreground" : "w-3 bg-hairline-strong hover:bg-mute"}`}
                />
              ))}
            </div>
          </figure>
        </div>
      </aside>
    </div>
  );
}

/** Heading block used at the top of every auth form. */
export function AuthHeading({ title, description }: { title: string; description?: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h1 className="text-[28px] leading-9 font-medium tracking-[-0.8px] text-foreground">{title}</h1>
      {description && <p className="mt-1.5 text-sm text-body">{description}</p>}
    </div>
  );
}

/** "or continue with email" style divider. */
export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-3" role="separator">
      <span className="h-px flex-1 bg-border" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
